import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import { MathJaxContext } from 'better-react-mathjax';
import Swal from 'sweetalert2';
import { renderBlocks } from '../components/renderBlocks';
import TimerDisplay from '../components/TimerDisplay';

const mathJaxConfig = {
  loader: { load: ['input/tex', 'output/chtml'] },
  tex: {
    inlineMath: [['$', '$'], ['\\(', '\\)']],
    displayMath: [['\\[', '\\]']]
  }
};

function formatTime(seconds) {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}m ${s < 10 ? '0' : ''}${s}s`;
}

export default function Quiz() {
  const timerRef = useRef();
  const navigate = useNavigate();
  const { outlineId } = useParams();

  const [outlineName, setOutlineName] = useState('');
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [checkAsYouGo, setCheckAsYouGo] = useState(false);
  const [scoreShown, setScoreShown] = useState(false);
  const [score, setScore] = useState(0);
  const [userEmail, setUserEmail] = useState(null);
  const [reviewMode, setReviewMode] = useState(false);

useEffect(() => {
  const queryParams = new URLSearchParams(window.location.search);
  const isReview = queryParams.get('review') === '1';
  setReviewMode(isReview);

  if (isReview) {
    setAnswers({});
    setSubmitted(false);
    setScoreShown(false);
    fetchQuestions(true);
  } else {
    fetchQuestions(false);
  }
}, [window.location.search]);


  useEffect(() => {
    const fetchUser = async () => {
      const { data } = await supabase.auth.getSession();
      const session = data?.session;
      if (session?.user) {
        setUserEmail(session.user.email);
      } else {
        setUserEmail(null);
      }
    };
    fetchUser();
  }, []);

  useEffect(() => {
    const checkLogin = async () => {
      const { data } = await supabase.auth.getSession();
      if (!data?.session) {
        Swal.fire({
          icon: 'warning',
          title: 'Login Required',
          text: 'Please login to attempt this quiz.',
          confirmButtonText: 'Go to Login'
        }).then(() => {
          localStorage.setItem('postLoginRedirect', window.location.pathname);
          navigate('/login');
        });
      }
    };
    checkLogin();
  }, [navigate]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    Swal.fire({
      icon: 'info',
      title: 'Logged Out',
      text: 'You have been successfully logged out.',
      confirmButtonText: 'OK'
    }).then(() => {
      navigate('/login');
    });
  };

  useEffect(() => {
    const fetchOutlineName = async () => {
      const { data, error } = await supabase
        .from('outline_summary')
        .select('outline_name')
        .eq('outline_id', outlineId)
        .single();
      if (!error && data) setOutlineName(data.outline_name);
    };
    if (outlineId) fetchOutlineName();
  }, [outlineId]);

  const fetchQuestions = async (onlyWrong = false) => {
    const { data: session } = await supabase.auth.getSession();
    const userId = session?.session?.user?.id;

    if (!userId || !outlineId) return;

    if (onlyWrong) {
      const { data: wrongList } = await supabase
        .from('wrong_answers')
        .select('question_id')
        .eq('user_id', userId)
        .eq('outline_id', outlineId);

      const ids = wrongList?.map((w) => w.question_id);
      if (!ids?.length) return setQuestions([]);

      const { data } = await supabase
        .from('quiz_questions')
        .select('*')
        .in('id', ids);

      if (data) {
        const shuffled = data.sort(() => Math.random() - 0.5);
        setQuestions(shuffled);
      }
    } else {
      const { data } = await supabase
        .from('quiz_questions')
        .select('*')
        .eq('outline_id', outlineId);

      if (data) {
        const shuffled = data.sort(() => Math.random() - 0.5);
        setQuestions(shuffled);
      }
    }
  };

  useEffect(() => {
    fetchQuestions();
  }, [outlineId]);

  useEffect(() => {
    if (
      checkAsYouGo &&
      Object.keys(answers).length === questions.length &&
      questions.length > 0
    ) {
      handleFinalScore();
    }
  }, [answers, checkAsYouGo, questions]);

  const handleOptionChange = (qId, selectedIndex) => {
    if (checkAsYouGo && answers[qId] === undefined) {
      setAnswers({ ...answers, [qId]: selectedIndex });
    } else if (!submitted) {
      setAnswers({ ...answers, [qId]: selectedIndex });
    }
  };

const handleFinalScore = async () => {
  let correct = 0;
  const wrongs = [];

  questions.forEach((q) => {
    const selected = answers[q.id];
    if (selected !== undefined && q.options[selected]?.is_correct) {
      correct++;
    } else {
      wrongs.push(q.id);
    }
  });

  const percent = Math.round((correct / questions.length) * 100);
  setScore(correct);
  setScoreShown(true);
  timerRef.current?.stop?.();

  const { data } = await supabase.auth.getSession();
  const userId = data?.session?.user?.id;
  if (!userId) return correct;

  if (!reviewMode) {
    const duration = timerRef.current?.getTime?.() || 0;

    await supabase.from('user_scores_detailed').insert({
      user_id: userId,
      outline_id: outlineId,
      total_questions: questions.length,
      attempted_questions: Object.keys(answers).length,
      correct_answers: correct,
      incorrect_answers: questions.length - correct,
      score_percent: percent,
      duration_seconds: duration,
      last_attempted_at: new Date().toISOString()
    });

    const { data: existingWrongs } = await supabase
      .from('wrong_answers')
      .select('question_id')
      .eq('user_id', userId)
      .eq('outline_id', outlineId);

    const existingIds = existingWrongs?.map((w) => w.question_id) || [];
    const newWrongs = wrongs.filter((qid) => !existingIds.includes(qid));

    if (newWrongs.length > 0) {
      const insertData = newWrongs.map((qid) => ({
        user_id: userId,
        question_id: qid,
        outline_id: outlineId
      }));
      await supabase.from('wrong_answers').insert(insertData);
    }
  }

  return correct;
};
const handleSubmit = async () => {
  if (Object.keys(answers).length < questions.length) {
    const result = await Swal.fire({
      title: 'Not all questions answered!',
      text: 'Would you still like to submit?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, submit',
      cancelButtonText: 'No, let me finish'
    });
    if (!result.isConfirmed) return;
  }

  setSubmitted(true);
  const correct = await handleFinalScore();

  await Swal.fire({
    icon: 'info',
    title: 'Quiz Submitted!',
    html: `🎯 You scored <strong>${correct}</strong> out of <strong>${questions.length}</strong>.<br><br>
           Would you like to <strong>review the questions you got wrong</strong>?<br>
           It's an effective way to revise and strengthen your learning.`,
    showCancelButton: true,
    confirmButtonText: '🔍 Review Wrong Answers So Far',
    cancelButtonText: 'No, Thanks',
    customClass: {
      popup: 'max-w-md'
    }
  }).then((result) => {
    if (result.isConfirmed) {
      navigate(`/quiz/${outlineId}?review=1`);
    }
  });
};

  return (
    <MathJaxContext config={mathJaxConfig}>
      <div className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-200 shadow-sm py-2 px-4 flex justify-between text-sm text-gray-700">
        <TimerDisplay ref={timerRef} />
        <h1 className="text-xl sm:text-2xl font-bold mb-2 text-gray-800 text-center">
          📘 {outlineName || 'Loading topic...'}
        </h1>
        <span>✅ {Object.keys(answers).length} / {questions.length} answered</span>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-20">
        <div className="text-right text-sm text-gray-600 mb-4 flex justify-between items-center">
          <div className="flex gap-4">
            <button
              onClick={() => {
                setReviewMode(true);
                setAnswers({});
                setSubmitted(false);
                setScoreShown(false);
                fetchQuestions(true);
              }}
              className="text-purple-700 border px-3 py-1 rounded hover:bg-purple-50"
            >
              🧐 Revise What You Got Wrong so far
              
            </button>
          </div>
          <div className="flex gap-4 items-center">
            <span>👋 Welcome, {userEmail || 'Anonymous'}!</span>
            {userEmail && (
              <button
                onClick={handleLogout}
                className="text-blue-600 hover:underline text-xs"
              >
                🔓 Logout
              </button>
            )}
          </div>
        </div>

        <h2 className="text-2xl font-bold mb-6 text-indigo-700">
          {reviewMode ? '🔁 Review Mode' : '📝 Quiz'}
        </h2>

        <div className="mb-6">
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={checkAsYouGo}
              onChange={() => {
                setCheckAsYouGo(!checkAsYouGo);
                setSubmitted(false);
                setScoreShown(false);
                setAnswers({});
                timerRef.current?.reset?.();
              }}
            />
            Check answers as you go
          </label>
        </div>

        {questions.map((q, i) => {
          const selectedIndex = answers[q.id];
          const showCheck = (checkAsYouGo && selectedIndex !== undefined) || (!checkAsYouGo && submitted);

          return (
            <div key={q.id} className="mb-8 p-4 border rounded shadow-sm bg-white">
              <div className="mb-2 text-gray-800 text-sm sm:text-base grid grid-cols-[auto,1fr] gap-2 items-start">
                <span className="font-semibold text-indigo-700">Q{i + 1}:</span>
                {renderBlocks([q.question_blocks[0]], null, 0)}
                {q.question_blocks.slice(1).map((block, idx) => (
                  <div key={idx} className="col-span-2 mt-2">
                    {renderBlocks([block], null, 0)}
                  </div>
                ))}
              </div>

              <div className="space-y-2 text-sm">
                {q.options.map((opt, idx) => {
                  const isSelected = answers[q.id] === idx;
                  const isCorrect = opt.is_correct;
                  const isMarkedCorrect = showCheck && isCorrect;
                  const isMarkedIncorrect = showCheck && isSelected && !isCorrect;

                  return (
                    <label
                      key={idx}
                      className={`block px-3 py-2 border rounded cursor-pointer ${
                        isMarkedCorrect ? 'bg-green-100 border-green-500' : ''
                      } ${isMarkedIncorrect ? 'bg-red-100 border-red-500' : 'border-gray-300'} ${
                        !showCheck && isSelected ? 'border-blue-400 bg-blue-50' : ''
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        <input
                          type="radio"
                          name={`q-${q.id}`}
                          className="mt-1"
                          checked={isSelected}
                          onChange={() => handleOptionChange(q.id, idx)}
                          disabled={checkAsYouGo && selectedIndex !== undefined}
                        />
                        <div className={isMarkedCorrect ? 'text-green-700 font-semibold' : ''}>
                          {renderBlocks(opt.blocks, null, 0)}
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>

              {showCheck && q.explanation_blocks && (
                <div className="mt-4 text-sm text-gray-700">
                  <strong>Explanation:</strong>
                  {renderBlocks(q.explanation_blocks, null, 0)}
                </div>
              )}
            </div>
          );
        })}

        {!submitted && !checkAsYouGo && questions.length > 0 && (
          <button
            onClick={handleSubmit}
            className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition"
          >
            Submit Quiz
          </button>
        )}

        {scoreShown && (
          <div className="mt-8 text-center text-lg font-semibold text-indigo-700">
            🎯 You scored {score} out of {questions.length} in {formatTime(timerRef.current?.getTime?.() || 0)}!
            
            <p className="text-sm mt-1 text-gray-600">
              {score === questions.length
                ? "🏆 Perfect score! You're a quiz master!"
                : score >= questions.length * 0.7
                ? "🔥 Great job! Almost perfect."
                : score >= questions.length * 0.4
                ? "👍 Good effort! Try again for a better score."
                : "📚 Don't worry, learning is a journey. Try again!"}
            </p>
                <div className="mt-4">
      <button
        onClick={() => navigate(`/quiz/${outlineId}?review=1`)}
        className="px-5 py-2 bg-yellow-500 text-white rounded hover:bg-yellow-600 transition"
      >
        🔍 Revise What You Got Wrong so far
      </button>
    </div>
          </div>
        )}
      </div>
    </MathJaxContext>
  );
}
